// Converts between a native `<input type="datetime-local">` value (a
// zone-less "YYYY-MM-DDTHH:mm" wall-clock string) and the UTC ISO string the
// API stores.
//
// The wall clock is the AUTHOR's own zone, and what gets stored is always the
// UTC instant it resolves to. An admin in Paris typing "1:00 PM" means 1:00 PM
// their time; a practitioner in Boston then sees that same instant as their own
// 7:00 AM. Nobody does offset arithmetic in their head, at either end - the
// zone label next to each field says which clock the number is in, and the
// stored value is zone-free.
//
// This does the conversion by hand rather than pulling in a date library:
// `Intl.DateTimeFormat` already knows the zone's offset, including DST, and is
// the only source of truth available in the browser without a dependency. The
// algorithm is zone-generic, so it stays correct for whatever zone the author
// happens to be in.

/** Where the zone lands when the runtime cannot resolve one. */
export const EVENT_FALLBACK_TIME_ZONE = 'America/Chicago';

/**
 * The authoring zone: the admin's own. Resolved per call rather than cached at
 * module load, so a long-lived tab carried across zones does not keep writing
 * the old one.
 */
export function authorTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || EVENT_FALLBACK_TIME_ZONE;
    } catch {
        return EVENT_FALLBACK_TIME_ZONE;
    }
}

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

/** The parts of an instant, as seen in `timeZone`. */
function zonedParts(date: Date, timeZone: string): Record<string, number> {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
    const parts: Record<string, number> = {};
    for (const { type, value } of formatter.formatToParts(date)) {
        if (type !== 'literal') {
            // Intl renders midnight as hour 24 in some engines; normalise it.
            parts[type] = value === '24' ? 0 : Number(value);
        }
    }
    return parts;
}

/**
 * How far `timeZone` sits from UTC at this instant, in ms. Derived by
 * formatting the instant into the zone and reading those wall-clock parts back
 * as if they were UTC - the difference is the offset, DST included.
 */
function zoneOffsetMs(date: Date, timeZone: string): number {
    const p = zonedParts(date, timeZone);
    const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asIfUtc - date.getTime();
}

/** UTC ISO -> author-zone wall-clock string for an input's `value`. Empty/invalid in, `''` out. */
export function isoToLocalInput(
    iso: string | null | undefined,
    timeZone: string = authorTimeZone()
): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const p = zonedParts(date, timeZone);
    return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * Author-zone wall-clock input string -> UTC ISO string. Empty/invalid in, `null` out.
 *
 * The offset is resolved twice: the first guess reads it at the wrong instant
 * when the entered time sits near a DST transition, so the second pass
 * re-reads it at the corrected instant. Without that, the two hours around a
 * spring-forward or fall-back land an hour off.
 */
export function localInputToIso(local: string, timeZone: string = authorTimeZone()): string | null {
    if (!local) return null;
    // Appending Z parses the wall clock AS IF it were UTC, which is exactly the
    // reference point the zone offset is then subtracted from. `datetime-local`
    // emits "YYYY-MM-DDTHH:mm", sometimes with seconds - both parse as-is.
    const parsed = Date.parse(`${local}Z`);
    if (Number.isNaN(parsed)) return null;

    const firstOffset = zoneOffsetMs(new Date(parsed), timeZone);
    let instant = parsed - firstOffset;
    const secondOffset = zoneOffsetMs(new Date(instant), timeZone);
    if (secondOffset !== firstOffset) {
        instant = parsed - secondOffset;
    }
    return new Date(instant).toISOString();
}

/**
 * The zone label shown next to every field - the abbreviation actually in force
 * for that date in the author's zone ("CDT" in summer, "CST" in winter), so the
 * caption never claims the wrong offset half the year. Load-bearing now that
 * the zone varies by who is authoring: it is the only thing on screen telling
 * the admin whose clock the number they typed is in. Falls back to the IANA name.
 */
export function eventTimeZoneLabel(
    at?: string | null,
    timeZone: string = authorTimeZone()
): string {
    const date = at ? new Date(at) : new Date();
    const when = Number.isNaN(date.getTime()) ? new Date() : date;
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short'
    }).formatToParts(when);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
}
